// See the screen (Commander redesign step 5b, Commander plan M5; ops plans/2026-10-17-commander-redesign, Decided f;
// lane SV design nodded by the coordinator 2026-10-17): a Radish support person watches one register's screen live from
// Commander. View only in this phase.
//   - Viewing needs no accept at the store, but the register shows a bar the whole time, and the store may stop it there.
//   - Staff start it (`fleet.startScreenView`): the cloud opens a session and sends the register a `screenView` device
//     command whose command id is the session id, only to a build declaring SCREEN_VIEW_READER (an older one answers
//     `unsupported`, so Commander can say the register's app is too old).
//   - The register posts frames to the cloud (SCREEN_PATHS.frames, its own device session); Commander reads the newest
//     one (SCREEN_PATHS.frame). The cloud keeps only the latest frame of each session, in memory: a frame is never
//     stored, logged or audited. Starting and stopping are audited like every Commander command.
//   - A session ends when staff stop it, the store stops it, the viewer or the register goes quiet, or at its cap.
//   - Card and PIN entry never leave the register: while such a screen shows, the register sends a placeholder instead.
import { z } from 'zod';
import { defineError } from '../../define/errors.ts';
import { defineCommand } from '../../define/operations.ts';
import { DeviceId, IsoInstant, LocationId } from '../../primitives/codecs.ts';
import { SupportCaseClosed, SupportCaseNotFound } from '../ops/support.ts';
import { DeviceNotFound, StepUpRequired } from './index.ts';

const SCREEN_CLOUD = "The cloud relays a register's screen to Commander; a frame lives only in the cloud's memory, never in a store or a database.";

/** The limits of a view session (coordinator, 2026-10-17). */
export const SCREEN_VIEW = {
  /** A view lasts at most this long; staff start another to keep watching. */
  maxSeconds: 1800,
  /**
   * A KDS screen's view lasts this long from its start, then ends, as legacy's KDS stream did (KDS-Q-19, G-K13 "keep the
   * effective value"): `stores/streaming.ts` ends the stream one `setTimeout` after `start()`, never reset by frames, at
   * `COMMANDER_IMAGE_SESSION_TIMEOUT` or 60 000 ms, and `quasar.config.js` never passes that variable into a build.
   */
  kdsMaxSeconds: 60,
  /** How long the register has to pick up the start (the device command's life). */
  startSeconds: 180,
  /** The session ends when Commander has not asked for a frame for this long. */
  viewerQuietSeconds: 30,
  /** The session ends when the register has not sent a frame for this long (after its first). */
  registerQuietSeconds: 30,
  /** A frame's encoded image at most (base64 of a JPEG). */
  maxFrameBytes: 262_144,
  /** At most this many frames a second from one session; more are refused. */
  maxFramesPerSecond: 4,
  /** Commander waits at most this long for a newer frame. */
  maxWaitMs: 10_000,
  /** At most this many sessions open at once in the cloud, and per staff member. */
  maxOpen: 20,
  maxOpenPerStaff: 3,
} as const;

/** Why a session ended, as Commander and the session record say it. */
export const ScreenViewEnd = z.enum(['stopped_by_staff', 'stopped_at_store', 'viewer_gone', 'register_gone', 'expired', 'unsupported', 'not_picked_up']).meta({ id: 'ScreenViewEnd' });
export type ScreenViewEnd = z.output<typeof ScreenViewEnd>;

/**
 * Control (lane SV, the control phase of Decided f; coordinator-nodded design and its seven conditions, 2026-10-17):
 * support asks, the store accepts on the register every time, and control lasts at most 15 minutes or until the case
 * closes. Taps and keys are injected only into the Radish app's own window; never while a hidden screen shows; never
 * into the store's own prompts.
 */
export const SCREEN_CONTROL = {
  /** Control lasts at most this long (Decided f). */
  maxSeconds: 900,
  /** The store has this long to accept; then it is declined by itself. */
  answerSeconds: 30,
  /** At most this many requests per session in `windowSeconds`, so a store that declined isn't nagged. */
  requestsPerWindow: 3,
  windowSeconds: 900,
  /** Input limits, per session. */
  maxInputsPerSecond: 10,
  maxInputsPerControl: 600,
  maxQueued: 20,
  /** While control is asked or runs, the register posts this often to take its orders and input. */
  frameIntervalMs: 250,
  /** A controllable register posts at least this often, so a request reaches it within a second. */
  heartbeatMs: 1000,
  /** At most this many `unchanged` heartbeats a second from one session. */
  maxHeartbeatsPerSecond: 8,
} as const;

/** Why control ended (or never started). */
export const ScreenControlEnd = z.enum(['expired', 'case_closed', 'stopped_by_staff', 'stopped_at_store', 'declined', 'timed_out', 'view_ended']).meta({ id: 'ScreenControlEnd' });
export type ScreenControlEnd = z.output<typeof ScreenControlEnd>;

/** A point on the register's screen, as a fraction of the frame's width and height. */
const Fraction = z.number().min(0).max(1);

/** One injected input: a tap, a long press, a swipe (scrolling), a few characters of text, or one key. */
export const ScreenInputEvent = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('tap'), x: Fraction, y: Fraction }),
  z.strictObject({ kind: z.literal('longPress'), x: Fraction, y: Fraction }),
  z.strictObject({ kind: z.literal('swipe'), x1: Fraction, y1: Fraction, x2: Fraction, y2: Fraction, ms: z.int().min(50).max(2000) }),
  z.strictObject({ kind: z.literal('text'), text: z.string().min(1).max(64).regex(/^[ -~]+$/) }),
  z.strictObject({ kind: z.literal('key'), key: z.enum(['enter', 'backspace', 'back']) }),
]).meta({ id: 'ScreenInputEvent' });
export type ScreenInputEvent = z.output<typeof ScreenInputEvent>;

/** An input with its place in the control period's order: applied at most once, in order. */
export const ScreenInput = z.strictObject({ seq: z.int().positive(), event: ScreenInputEvent }).meta({ id: 'ScreenInput' });
export type ScreenInput = z.output<typeof ScreenInput>;

/** Commander's input for one session (SCREEN_PATHS.input). */
export const ScreenInputUpload = z.strictObject({
  sessionId: z.uuid(), accountId: z.uuid(), locationId: z.uuid(),
  inputs: z.array(ScreenInput).min(1).max(SCREEN_CONTROL.maxInputsPerSecond)
    .refine((inputs) => new Set(inputs.map((i) => i.seq)).size === inputs.length, 'each seq at most once'),
}).meta({ id: 'ScreenInputUpload' });
export type ScreenInputUpload = z.output<typeof ScreenInputUpload>;

/** The cloud's answer to Commander's input: how many were queued for the register. */
export const ScreenInputAck = z.strictObject({ queued: z.int().nonnegative() }).meta({ id: 'ScreenInputAck' });
export type ScreenInputAck = z.output<typeof ScreenInputAck>;

/** Control as the register hears it in a frame's answer (only when its upload says it `controls`). */
export const ScreenControlOrder = z.strictObject({
  /** `asked`: show the store the accept prompt (until `until`); `active`: apply `inputs`; `none`: no control. */
  state: z.enum(['none', 'asked', 'active']),
  requestId: z.uuid().nullable(),
  /** When the store's answer is due (asked), or when control ends (active). */
  until: IsoInstant.nullable(),
  inputs: z.array(ScreenInput).max(SCREEN_CONTROL.maxQueued),
}).meta({ id: 'ScreenControlOrder' });
export type ScreenControlOrder = z.output<typeof ScreenControlOrder>;

/** Control as Commander sees it beside the frame. */
export const ScreenControlView = z.strictObject({
  state: z.enum(['none', 'asked', 'active', 'ended']),
  /** When the store's answer is due (asked), or when control ends (active). */
  until: IsoInstant.nullable(),
  ended: ScreenControlEnd.nullable(),
  /** How many inputs this control period applied so far (a count only; inputs are never kept). */
  inputs: z.int().nonnegative(),
}).meta({ id: 'ScreenControlView' });
export type ScreenControlView = z.output<typeof ScreenControlView>;

/** The raw routes the frames travel on (cloud only; invariant A14's SCREEN_ROUTES allow-list). */
export const SCREEN_PATHS = {
  /** The register posts one frame (its device session, DPoP). */
  frames: '/v1/screen/frames',
  /** Commander reads the newest frame of one session (its staff session). */
  frame: '/v1/screen/frame',
  /** Commander sends input while control runs (its staff session). */
  input: '/v1/screen/input',
} as const;

/**
 * Why a register's screen is hidden, in one word and nothing more (Q's check, 2026-10-17): a staff member signing in
 * or approving with a PIN, a card payment, or another card or secret screen.
 */
export const ScreenHiddenReason = z.enum(['staff_sign_in', 'card_payment', 'other']).meta({ id: 'ScreenHiddenReason' });
export type ScreenHiddenReason = z.output<typeof ScreenHiddenReason>;

/**
 * What a register posts: one frame of a session, or a placeholder while a card or PIN screen shows (`hidden`, no image),
 * or `ended` when the store stopped the view on the register. `seq` rises by one each frame.
 */
export const ScreenFrameUpload = z.strictObject({
  sessionId: z.uuid(),
  /** The register can be controlled (a build with the control phase): its answers may then carry `control`. */
  controls: z.literal(true).optional(),
  seq: z.int().positive(),
  capturedAt: IsoInstant,
  frame: z.discriminatedUnion('kind', [
    z.strictObject({
      kind: z.literal('image'),
      width: z.int().min(1).max(4096),
      height: z.int().min(1).max(4096),
      /** The JPEG, base64. */
      jpeg: z.string().min(4).max(SCREEN_VIEW.maxFrameBytes).regex(/^[A-Za-z0-9+/]+={0,2}$/),
    }),
    /** A card number, a PIN or a password is on the screen: nothing of it leaves the register. */
    z.strictObject({ kind: z.literal('hidden'), reason: ScreenHiddenReason.optional() }),
    /** The store pressed Stop on the bar. */
    z.strictObject({ kind: z.literal('ended') }),
    /**
     * The screen has not changed since the register's last frame: a heartbeat a controllable register sends often (at
     * least every second, every quarter second while control is asked or runs) to hear control's orders and input.
     */
    z.strictObject({
      kind: z.literal('unchanged'),
      /** Why the screen is hidden now (null: it is shown), so the cloud always knows whether input may apply. */
      hidden: ScreenHiddenReason.nullable().optional(),
    }),
  ]),
}).meta({ id: 'ScreenFrameUpload' });
export type ScreenFrameUpload = z.output<typeof ScreenFrameUpload>;

/** The cloud's answer to a frame: keep sending (no sooner than `nextAfterMs`), or stop (the session is over). */
export const ScreenFrameAck = z.strictObject({
  continue: z.boolean(),
  nextAfterMs: z.int().nonnegative(),
  ended: ScreenViewEnd.nullable(),
  /** Control, only for a register whose upload said it `controls` (an older one would refuse the field). */
  control: ScreenControlOrder.optional(),
  /** The cloud holds no picture of this screen (after a restart): send a full frame next. Only to a register that `controls`. */
  wantFrame: z.literal(true).optional(),
}).meta({ id: 'ScreenFrameAck' });
export type ScreenFrameAck = z.output<typeof ScreenFrameAck>;

/** What Commander reads: the session's state and its newest frame newer than the one it has, if any came in time. */
export const ScreenFrameView = z.strictObject({
  sessionId: z.uuid(),
  state: z.enum(['waiting', 'live', 'ended']),
  ended: ScreenViewEnd.nullable(),
  /** When the session ends by itself at the latest. */
  expiresAt: IsoInstant,
  frame: z.strictObject({
    seq: z.int().positive(),
    capturedAt: IsoInstant,
    hidden: z.boolean(),
    /** Why it is hidden (null when it isn't, or the register's build doesn't say). */
    hiddenReason: ScreenHiddenReason.nullable().optional(),
    width: z.int().nullable(),
    height: z.int().nullable(),
    jpeg: z.string().nullable(),
  }).nullable(),
  /** Control beside the view; absent from a cloud before the control phase. */
  control: ScreenControlView.optional(),
  /** Whether this register's build can be controlled (it said so with its frames). */
  controllable: z.boolean().optional(),
}).meta({ id: 'ScreenFrameView' });
export type ScreenFrameView = z.output<typeof ScreenFrameView>;

/** As many screens are being watched as the cloud allows (in all, or by this staff member). */
export const ScreenViewBusy = defineError({
  code: 'fleet.screen_view_busy', httpStatus: 429, message: 'errors.fleet.screen_view_busy', retryable: true,
  details: z.object({ open: z.int(), limit: z.int() }),
});

/**
 * The device can't share its screen: only the register app (a register or a handheld) does, so a KDS screen or a menu
 * display is refused at the start instead of being asked and told its app is too old (review of B554).
 */
export const ScreenViewDeviceCannotShare = defineError({
  code: 'fleet.screen_view_device_cannot_share', httpStatus: 409, message: 'errors.fleet.screen_view_device_cannot_share', retryable: false,
  details: z.object({ deviceId: DeviceId, formFactor: z.string() }),
});

/** No open view session has this id for this staff member. */
export const ScreenViewNotFound = defineError({
  code: 'fleet.screen_view_not_found', httpStatus: 404, message: 'errors.fleet.screen_view_not_found', retryable: false,
  details: z.object({ sessionId: z.uuid() }),
});

export const StartScreenView = defineCommand({
  name: 'fleet.startScreenView',
  summary: "Watch one register's screen live from Commander (view only): the register shows a bar the whole time, the store can stop it there, and it ends by itself after 30 minutes (a KDS screen's after 1 minute).",
  input: z.object({
    /** The location's account: Radish staff act in it from Commander (`staffAnyLocation`). */
    accountId: z.uuid(),
    locationId: LocationId,
    deviceId: DeviceId,
    /** The support case this is done in (its location's, open): recorded when given. */
    caseId: z.uuid().optional(),
  }),
  output: z.object({ sessionId: z.uuid(), expiresAt: IsoInstant }),
  permission: 'fleet.screen.view',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: SCREEN_CLOUD,
  emits: [],
  errors: [DeviceNotFound, SupportCaseNotFound, SupportCaseClosed, ScreenViewBusy, ScreenViewDeviceCannotShare],
  risk: 'high',
  dryRun: true,
  // A person's click in Commander starts a view; Claude's pane never does (coordinator, 2026-10-17).
  agent: { expose: false },
  staffAnyLocation: true,
});

export const StopScreenView = defineCommand({
  name: 'fleet.stopScreenView',
  summary: "Stop watching a register's screen: the register takes its bar down once its last frame is refused.",
  input: z.object({ accountId: z.uuid(), locationId: LocationId, sessionId: z.uuid() }),
  output: z.object({ sessionId: z.uuid(), ended: ScreenViewEnd }),
  permission: 'fleet.screen.view',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: SCREEN_CLOUD,
  emits: [],
  errors: [ScreenViewNotFound],
  risk: 'low',
  dryRun: false,
  agent: { expose: false },
  staffAnyLocation: true,
});

/** The register's build can't be controlled (it never said it `controls`). */
export const ScreenControlUnsupported = defineError({
  code: 'fleet.screen_control_unsupported', httpStatus: 409, message: 'errors.fleet.screen_control_unsupported', retryable: false,
  details: z.object({ sessionId: z.uuid() }),
});

/** Control is already asked for or running on this view, was asked for too often, or the view has ended. */
export const ScreenControlRefused = defineError({
  code: 'fleet.screen_control_refused', httpStatus: 409, message: 'errors.fleet.screen_control_refused', retryable: true,
  details: z.object({ sessionId: z.uuid(), why: z.enum(['already', 'too_often', 'view_ended']) }),
});

/** No control request with this id is open for this register. */
export const ScreenControlNotFound = defineError({
  code: 'fleet.screen_control_not_found', httpStatus: 404, message: 'errors.fleet.screen_control_not_found', retryable: false,
  details: z.object({ sessionId: z.uuid() }),
});

export const RequestScreenControl = defineCommand({
  name: 'fleet.requestScreenControl',
  summary: "Ask the store to let Radish support control a register's screen, after a step-up: the register asks its staff to accept, every time; control lasts at most 15 minutes or until the case closes, and the store can stop it.",
  input: z.object({
    accountId: z.uuid(),
    locationId: LocationId,
    sessionId: z.uuid(),
    /** The open support case this is done in, at that location (required: control ends when it closes). */
    caseId: z.uuid(),
  }),
  output: z.object({ sessionId: z.uuid(), requestId: z.uuid(), answerBy: IsoInstant }),
  permission: 'fleet.screen.control',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: SCREEN_CLOUD,
  emits: [],
  errors: [ScreenViewNotFound, SupportCaseNotFound, SupportCaseClosed, ScreenControlUnsupported, ScreenControlRefused, StepUpRequired],
  risk: 'high',
  dryRun: true,
  // A person's click in Commander asks; Claude's pane never does (coordinator condition 6).
  agent: { expose: false },
  staffAnyLocation: true,
});

export const StopScreenControl = defineCommand({
  name: 'fleet.stopScreenControl',
  summary: "Give control of a register's screen back to the store (the view goes on).",
  input: z.object({ accountId: z.uuid(), locationId: LocationId, sessionId: z.uuid() }),
  output: z.object({ sessionId: z.uuid(), ended: ScreenControlEnd.nullable() }),
  permission: 'fleet.screen.control',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: SCREEN_CLOUD,
  emits: [],
  errors: [ScreenViewNotFound],
  risk: 'low',
  dryRun: false,
  agent: { expose: false },
  staffAnyLocation: true,
});

export const AnswerScreenControl = defineCommand({
  name: 'fleet.answerScreenControl',
  summary: "The store's answer on the register to Radish support's request to control it: accepted, declined, timed out, or stopped from the bar.",
  input: z.object({ locationId: LocationId, sessionId: z.uuid(), requestId: z.uuid(), answer: z.enum(['accepted', 'declined', 'timed_out', 'stopped']) }),
  output: z.object({ sessionId: z.uuid(), state: z.enum(['active', 'ended']) }),
  permission: { session: 'device' },
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: SCREEN_CLOUD,
  emits: [],
  errors: [ScreenControlNotFound],
  risk: 'medium',
  dryRun: true,
  agent: { expose: false },
});

export const screenCommands = [StartScreenView, StopScreenView, RequestScreenControl, StopScreenControl, AnswerScreenControl] as const;
